import React, { useState, useEffect } from 'react';
import { Modal, Alert, message } from 'antd';
import { useTranslation } from 'react-i18next';
import ShippingTypeSelect from '@shared/selects/ShippingTypeSelect';

/**
 * ChangeShippingTypeModal - 變更 fulfillment 物流類型的 Modal
 *
 * @param {boolean} visible - 是否顯示
 * @param {Function} onCancel - 取消回調
 * @param {Function} onSuccess - 成功回調
 * @param {Object} fulfillment - 目標 fulfillment（需包含 id, shipping_type, merchant_id, is_cod, shipments）
 * @param {string} [role='operator'] - 'operator' | 'admin'
 */
export default function ChangeShippingTypeModal({
  visible,
  onCancel,
  onSuccess,
  fulfillment,
  role = 'operator',
}) {
  const { t } = useTranslation();
  const [selectedShippingTypeId, setSelectedShippingTypeId] = useState(null);
  const [loading, setLoading] = useState(false);

  // 計算 system shipments 數量（用於警告）
  const systemShipmentCount = (fulfillment?.shipments || []).filter(
    (s) => s.source === 'system' && !s.deleted_at
  ).length;

  useEffect(() => {
    if (visible) {
      setSelectedShippingTypeId(null);
    }
  }, [visible]);

  const handleConfirm = async () => {
    if (!selectedShippingTypeId) {
      message.warning(t('common.select.please_select'));
      return;
    }

    if (selectedShippingTypeId === fulfillment?.shipping_type?.id) {
      message.warning(t('shared.fulfillments.change_shipping_type.same_type_warning', { defaultValue: '請選擇不同的物流類型' }));
      return;
    }

    setLoading(true);
    try {
      let service;
      if (role === 'admin') {
        const mod = await import('@services/admin/fulfillmentService');
        service = mod.fulfillmentService;
      } else {
        const mod = await import('@services/operator/fulfillmentService');
        service = mod.fulfillmentService;
      }

      const result = await service.changeShippingType(fulfillment.id, selectedShippingTypeId);
      if (result.success) {
        message.success(t('shared.fulfillments.change_shipping_type.success', { defaultValue: '物流類型已變更' }));
        onSuccess?.();
      } else {
        message.error(t(`errors.${result.message}`, { defaultValue: result.message }));
      }
    } catch (error) {
      console.error('[ChangeShippingTypeModal] failed:', error);
      message.error(t('common.error.operation_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('shared.fulfillments.change_shipping_type.title', { defaultValue: '變更物流類型' })}
      open={visible}
      onCancel={onCancel}
      onOk={handleConfirm}
      confirmLoading={loading}
      okText={t('shared.fulfillments.change_shipping_type.confirm', { defaultValue: '確認變更' })}
      cancelText={t('common.cancel')}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8 }}>
          {t('shared.fulfillments.change_shipping_type.new_type_label', { defaultValue: '新物流類型' })}
        </div>
        <ShippingTypeSelect
          merchantId={fulfillment?.merchant_id}
          codOnly={fulfillment?.is_cod || false}
          currentShippingTypeId={fulfillment?.shipping_type?.id}
          value={selectedShippingTypeId}
          onChange={setSelectedShippingTypeId}
          role={role}
          style={{ width: '100%' }}
        />
      </div>

      {systemShipmentCount > 0 && (
        <Alert
          type="warning"
          showIcon
          message={t('shared.fulfillments.change_shipping_type.shipment_warning', {
            count: systemShipmentCount,
            defaultValue: `系統產生的託運單將自動作廢（${systemShipmentCount}筆）`,
          })}
        />
      )}
    </Modal>
  );
}
